// Choosing a prop firm's plan: type the firm (FirmSearch pops up the matches, nicknames
// included), then pick the plan and the size (PlanPicker). Used by Add account, the account
// settings and the free report card, so the list can grow without a long dropdown.
import { useEffect, useRef, useState } from 'react';
import { FIRM_INFO, PRESETS, PRESET_FIRMS } from './balance';
import { useI18n } from './i18n';
import { LogoBadge } from './Logos';

const k = (n) => `${Math.round(n / 1000)}K`;
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
export const firmName = (firm) => (FIRM_INFO[firm] && FIRM_INFO[firm].name) || firm;

// Firms whose name or nickname contains what was typed, the ones that start with it first.
export function searchFirms(query, firms = PRESET_FIRMS) {
  const q = norm(query);
  if (!q) return firms;
  const names = (f) => [f, firmName(f), ...((FIRM_INFO[f] && FIRM_INFO[f].aliases) || [])].map(norm);
  const starts = firms.filter((f) => names(f).some((n) => n.startsWith(q)));
  const contains = firms.filter((f) => !starts.includes(f) && names(f).some((n) => n.includes(q)));
  return [...starts, ...contains];
}

// The plans a firm offers (in order), and the sizes in each, for the account type if one is set.
export function firmPlans(firm, type = null) {
  const list = PRESETS.filter((p) => p.firm === firm && (!type || p.type === type));
  const plans = [...new Set(list.map((p) => p.plan))];
  return plans.map((plan) => ({ plan, presets: list.filter((p) => p.plan === plan).sort((a, b) => a.size - b.size) }));
}

function summary(firm) {
  const plans = firmPlans(firm);
  const sizes = [...new Set(PRESETS.filter((p) => p.firm === firm).map((p) => p.size))].sort((a, b) => a - b);
  return `${plans.map((p) => p.plan).join(' · ')} — ${sizes.length > 1 ? `${k(sizes[0])}–${k(sizes[sizes.length - 1])}` : k(sizes[0])}`;
}

// A search box for the firm. `value` is the chosen firm ('' for none); `allowNone` adds
// "Another firm, or my own rules". `firms` limits the list (the firms on the account's platform).
export function FirmSearch({ value, onChange, allowNone = true, id = 'firm-search', autoFocus = false, firms = PRESET_FIRMS }) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef(null);
  const box = useRef(null);
  const matches = searchFirms(query, firms);
  const options = [...matches, ...(allowNone ? [''] : [])];

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function choose(firm) {
    onChange(firm);
    setQuery('');
    setOpen(false);
  }

  function onKey(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
    } else if (e.key === 'Enter' && open && options.length) {
      e.preventDefault();
      choose(options[Math.min(active, options.length - 1)]);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
    }
  }

  if (value && !open) {
    return (
      <div className="firm-chosen">
        <LogoBadge logo={value} size="pf-sm" />
        <span className="firm-chosen-name">{firmName(value)}</span>
        <button type="button" className="link-button" onClick={() => { setOpen(true); setActive(0); setTimeout(() => input.current && input.current.focus(), 0); }}>{t('Change')}</button>
      </div>
    );
  }

  return (
    <div className="firm-search" ref={box}>
      <input ref={input} id={id} type="search" role="combobox" aria-expanded={open} aria-controls={`${id}-list`} aria-autocomplete="list"
        autoComplete="off" autoFocus={autoFocus} value={query} placeholder={t('Start typing your firm, e.g. Apex')}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)} onKeyDown={onKey} />
      {open && (
        <ul className="firm-options" id={`${id}-list`} role="listbox">
          {matches.map((f, i) => (
            <li key={f} role="option" aria-selected={i === active} className={i === active ? 'active' : ''}
              onMouseDown={(e) => { e.preventDefault(); choose(f); }} onMouseEnter={() => setActive(i)}>
              <LogoBadge logo={f} size="pf-sm" />
              <span className="firm-option-text"><strong>{firmName(f)}</strong>
                <small>{summary(f)}</small></span>
            </li>
          ))}
          {!matches.length && <li className="firm-none">{t('No saved rules for “{query}” yet. Pick “Another firm” and enter them yourself.', { query })}</li>}
          {allowNone && (
            <li role="option" aria-selected={active === matches.length} className={`firm-other${active === matches.length ? ' active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); choose(''); }} onMouseEnter={() => setActive(matches.length)}>
              <strong>{t('Another firm, or my own rules')}</strong>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// The firm's plans (when it has more than one) and their sizes. `presetId` is the chosen one.
export function PlanPicker({ firm, type = null, presetId, onPick }) {
  const { t } = useI18n();
  const plans = firmPlans(firm, type);
  const chosen = PRESETS.find((p) => p.id === presetId);
  const [plan, setPlan] = useState(chosen && chosen.firm === firm ? chosen.plan : null);
  if (!plans.length) return null;
  const current = plans.find((p) => p.plan === plan) || plans[0];
  // A firm's own plan name stays as is (Lucid's Daily, Direct); only the words after it translate.
  const planLabel = (p) => (p.presets[0].brand ? p.plan.split(' ').map((w, i) => (i ? t(w) : w)).join(' ') : t(p.plan));
  return (
    <div className="plan-picker">
      {plans.length > 1 && (
        <div className="plan-series" role="radiogroup" aria-label={t('Plan')}>
          {plans.map((p) => (
            <button key={p.plan} type="button" role="radio" aria-checked={p.plan === current.plan} className={p.plan === current.plan ? 'on' : ''}
              onClick={() => setPlan(p.plan)}>{planLabel(p)}</button>
          ))}
        </div>
      )}
      <div className="plan-sizes" role="radiogroup" aria-label={t('Account size')}>
        {current.presets.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={presetId === p.id} className={presetId === p.id ? 'on' : ''}
            onClick={() => onPick(p)}>{k(p.size)}</button>
        ))}
      </div>
    </div>
  );
}
